/**
 * Utility functions for @-mention functionality
 */

/** Regex pattern for detecting @mentions in text */
export const MENTION_REGEX = /@([a-zA-Z0-9_]+)/g

/** Represents a segment of parsed text */
export interface MentionSegment {
    type: "text" | "mention"
    text: string
    username?: string // Only set for mention segments
}

/**
 * Extract all @usernames from text
 * @param text - The text to search for mentions
 * @returns Array of unique usernames (without the @ symbol)
 */
export function extractMentions(text: string): string[] {
    if (!text) return []

    const matches = text.matchAll(MENTION_REGEX)
    const usernames = [...matches].map((match) => match[1]).filter((u): u is string => u !== undefined)

    // Return unique usernames
    return [...new Set(usernames)]
}

/**
 * Parse text into segments of plain text and mentions
 * This is useful for rendering text with styled mentions
 * @param text - The text to parse
 * @returns Array of segments
 */
export function parseMentionsInText(text: string): MentionSegment[] {
    if (!text) return []

    const segments: MentionSegment[] = []
    let lastIndex = 0

    // Reset regex state
    const regex = new RegExp(MENTION_REGEX.source, "g")
    let match: RegExpExecArray | null

    while ((match = regex.exec(text)) !== null) {
        // Add text before this mention
        if (match.index > lastIndex) {
            segments.push({
                type: "text",
                text: text.slice(lastIndex, match.index),
            })
        }

        // Add the mention
        segments.push({
            type: "mention",
            text: match[0], // Full match including @
            username: match[1] ?? "", // Just the username
        })

        lastIndex = match.index + match[0].length
    }

    // Add remaining text after last mention
    if (lastIndex < text.length) {
        segments.push({
            type: "text",
            text: text.slice(lastIndex),
        })
    }

    return segments
}

/**
 * Check if text contains any @mentions
 * @param text - The text to check
 * @returns true if text contains at least one @mention
 */
export function containsMentions(text: string): boolean {
    if (!text) return false
    // Use a new regex instance to avoid global flag lastIndex issues
    const regex = new RegExp(MENTION_REGEX.source)
    return regex.test(text)
}

/**
 * Get the word being typed after @ for autocomplete
 * Returns null if cursor is not in a mention context
 * @param text - Full text value
 * @param cursorPosition - Current cursor position
 * @returns Object with query string and start position, or null
 */
export function getMentionQuery(
    text: string,
    cursorPosition: number
): { query: string; startPosition: number } | null {
    if (!text || cursorPosition <= 0) return null

    // Look backwards from cursor to find @
    let startPos = cursorPosition - 1
    while (startPos >= 0) {
        const char = text[startPos]
        if (char === undefined) break

        // Found @, return the query
        if (char === "@") {
            const query = text.slice(startPos + 1, cursorPosition)
            // Make sure query only contains valid username characters
            if (/^[a-zA-Z0-9_]*$/.test(query)) {
                return { query, startPosition: startPos }
            }
            return null
        }

        // Hit a space or other non-username character before finding @
        if (!/[a-zA-Z0-9_]/.test(char)) {
            return null
        }

        startPos--
    }

    return null
}

/**
 * Insert a mention into text at the given position, replacing the partial query
 * @param text - Current text value
 * @param username - Username to insert (without @)
 * @param mentionStartPosition - Position where @ was typed
 * @param cursorPosition - Current cursor position
 * @returns Object with new text and new cursor position
 */
export function insertMention(
    text: string,
    username: string,
    mentionStartPosition: number,
    cursorPosition: number
): { newText: string; newCursorPosition: number } {
    const before = text.slice(0, mentionStartPosition)
    const after = text.slice(cursorPosition)
    const mention = `@${username}`

    // Add a space after the mention
    const newText = `${before}${mention} ${after}`
    const newCursorPosition = mentionStartPosition + mention.length + 1

    return { newText, newCursorPosition }
}

/** Club member with user profile data for mention mapping */
export interface ClubMemberForMention {
    user_id: string
    user_profiles?: {
        username?: string | null
        first_name?: string | null
        last_name?: string | null
        display_name?: string | null
    } | null
}

/** Data stored for each mention in the map */
export interface MentionUserData {
    userId: string
    displayName: string
}

/** Map of lowercase usernames to user data */
export type MentionMap = Record<string, MentionUserData>

/**
 * Build a MentionMap from club members data
 * Maps lowercase usernames to user data (ID and display name) for rendering mentions
 * @param members - Array of club members with user profile data
 * @returns Record mapping lowercase usernames to user data
 */
export function buildMentionMap(members: ClubMemberForMention[] | undefined): MentionMap {
    if (!members) return {}

    const map: MentionMap = {}
    for (const member of members) {
        const profile = member.user_profiles
        const username = profile?.username
        if (username && member.user_id) {
            // Build display name: prefer display_name, then "First Last", then username
            const displayName =
                profile?.display_name ||
                [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
                username

            map[username.toLowerCase()] = {
                userId: member.user_id,
                displayName,
            }
        }
    }
    return map
}
